import { Component, ChangeDetectorRef, OnInit, OnDestroy } from '@angular/core';
import { MainHeadingSubtextComponent } from '../../atoms/main-heading-subtext/main-heading-subtext.component';
import { SendoutService } from '../../../services/sendout/sendout.service';
import { SnackbarService } from '../../../services/snackbar/snackbar.service';
import {
  Sendout,
  Filter,
  GroupedDataSet,
  Event,
  SendoutStatus,
  SendoutViewModel,
  SendoutType,
} from '../../../types';
import {
  GlobalDataService,
  SessionData,
} from '../../../services/global-data/global-data.service';
import { Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { SearchFieldComponent } from '../../atoms/search-field/search-field.component';
import { TableColumns } from '../../../types';
import { SkeletonComponent } from '../../molecules/skeleton/skeleton.component';
import { sendoutFilters } from '../../../constants/filters.constants';
import { SharedFunctionalityService } from '../../../services/shared-functionality/shared-functionality.service';
import { Subscription } from 'rxjs';
import { differenceInDays, startOfDay, sub } from 'date-fns';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { SendoutTableComponent } from '../../molecules/sendout-table/sendout-table.component';
import { MatExpansionModule } from '@angular/material/expansion';
import { StrokedButtonComponent } from '../../atoms/stroked-button/stroked-button.component';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

interface LookbackPeriod {
  label: string;
  sub: {
    days?: number;
    weeks?: number;
    months?: number;
    years?: number;
  };
}

@Component({
  selector: 'app-email-sendout-manager',
  standalone: true,
  imports: [
    MainHeadingSubtextComponent,
    CommonModule,
    SearchFieldComponent,
    SkeletonComponent,
    FormsModule,
    MatSlideToggleModule,
    SendoutTableComponent,
    MatExpansionModule,
    StrokedButtonComponent,
    RouterLink,
    MatSelectModule,
    MatButtonToggleModule,
  ],
  templateUrl: './email-sendout-manager.component.html',
  styleUrl: './email-sendout-manager.component.css',
})
export class EmailSendoutManagerComponent implements OnInit, OnDestroy {

  sessionDataSubscription?: Subscription;
  sessionData!: SessionData;

  isLoading: boolean = false;
  slideToggleState: boolean = true;

  cardButtonText: string = 'Se udsendelse';
  emptyStateText: string = 'Ingen udsendelser';
  cardTitle: string = 'Udsendelse';

  allSendouts: SendoutViewModel[] = [];
  sendouts: SendoutViewModel[] = [];

  allSendoutsAccordion: GroupedDataSet<SendoutViewModel> = null!;
  sendoutsAccordion: GroupedDataSet<SendoutViewModel> = null!;

  events: Event[] = [];

  lookbackOptions: LookbackPeriod[] = [
    { label: '3 måneder', sub: { months: 3 } },
    { label: '6 måneder', sub: { months: 6 } },
    { label: '12 måneder', sub: { months: 12 } },
    { label: 'Alt', sub: { years: 10 } },
  ];

  selectedLookback: LookbackPeriod = this.lookbackOptions[0];

  statusFilters: Filter[] = sendoutFilters;
  statusFiltersAll: Filter[] = sendoutFilters;

  columns: TableColumns[] = [
    { field: 'sendoutName', header: 'Navn', sort: false },
    { field: 'store', header: 'Butik', sort: false },
    { field: 'before', header: 'Dage fra foregående udsendelse', sort: false },
    { field: 'sendoutDate', header: 'Udsendelsesdato', sort: true },
    { field: 'after', header: 'Dage til næste udsendelse', sort: false },
    { field: 'createdAt', header: 'Oprettelsesdato ', sort: true },
    { field: 'contactPerson', header: 'Kontaktperson ', sort: false },
    { field: 'reviewedByName', header: 'Godkendt/afvist af', sort: false },
  ];

  constructor(
    private changeDetectorRef: ChangeDetectorRef,
    private sendoutService: SendoutService,
    private snackbarService: SnackbarService,
    private globalDataService: GlobalDataService,
    private router: Router,
    private sharedFunctionalityService: SharedFunctionalityService
  ) {}

  ngOnInit(): void {
    this.sessionDataSubscription = this.globalDataService
      .getSessionData()
      .subscribe((sessionData) => {
        this.sessionData = sessionData;
        this.getAllSendouts();
      });
  }

  ngOnDestroy(): void {
    this.sessionDataSubscription?.unsubscribe();
  }

  applySearchFilter(filterValue: string) {
    const { filteredItems, slideToggleState } =
      this.sharedFunctionalityService.applySearchFilter(
        filterValue,
        this.allSendouts,
        ['sendoutName', 'stores.[*].id', 'stores.[*].address'],
        this.changeDetectorRef,
        this.slideToggleState
      );
    this.sendouts = filteredItems;
    this.slideToggleState = slideToggleState;
  }

  onSlideToggleChange() {
    this.slideToggleState = this.sharedFunctionalityService.onSlideToggleChange(
      this.slideToggleState
    );
  }

  sendoutClick = async (sendout: Sendout) => {
    if (sendout.sendoutType == SendoutType.SingleStore) {
      this.router.navigate(['sendout', sendout.id]);
    } else if (sendout.sendoutType == SendoutType.MultiStore) {
      this.router.navigate(['multi-store-sendout', sendout.id]);
    }
  };

  processSendouts(data: Sendout[]): SendoutViewModel[] {
    data.sort((a, b) => a.sendoutDate?.getTime() - b.sendoutDate?.getTime());

    const groupedByStore: Map<number, Sendout[]> = data
      .filter((s) => s.sendoutType == SendoutType.SingleStore)
      .reduce((acc, sendout) => {
        if (sendout.storeIds[0]) {
          if (!acc.has(sendout.storeIds[0])) {
            acc.set(sendout.storeIds[0], []);
          }
          acc.get(sendout.storeIds[0])?.push(sendout);
        }
        return acc;
      }, new Map<number, Sendout[]>());

    const processedData: SendoutViewModel[] = [];

    for (const [storeId, sendouts] of groupedByStore) {
      sendouts.forEach((sendout, index) => {
        const before: number | null =
          index > 0
            ? differenceInDays(
                sendout.sendoutDate,
                sendouts[index - 1].sendoutDate
              )
            : null;
        const after: number | null =
          index < sendouts.length - 1
            ? differenceInDays(
                sendouts[index + 1].sendoutDate,
                sendout.sendoutDate
              )
            : null;

        processedData.push({
          ...sendout,
          before,
          after,
        });
      });
    }

    // Insert multi-store sendouts
    data
      .filter((s) => s.sendoutType == SendoutType.MultiStore)
      .forEach((sendout) => {
        processedData.push({
          ...sendout,
          before: null,
          after: null,
        });
      });

    processedData.sort(
      (a, b) => a.sendoutDate?.getTime() - b.sendoutDate?.getTime()
    );

    return processedData;
  }

  getAllSendouts = () => {
    this.isLoading = true;
    const today = startOfDay(new Date());
    const fromDate = sub(today, this.selectedLookback.sub);
    console.log({ s: this.selectedLookback.sub });
    const toDate = null;
    this.sendoutService.getSendouts(fromDate, toDate).subscribe({
      next: (data) => {
        this.allSendouts = this.processSendouts(
          data.filter(
            (e) =>
              !(
                e.sendoutType == SendoutType.SingleStore &&
                e.status == SendoutStatus.Draft
              ) && e.status !== SendoutStatus.Expired
          )
        );

        this.sendouts = [...this.allSendouts];

        this.allSendoutsAccordion =
          this.sharedFunctionalityService.getGroupedDataSet(
            this.allSendouts,
            [
              {
                id: SendoutStatus.Draft,
                label: 'Kladde',
              },
              {
                id: SendoutStatus.AwaitingApproval,
                label: 'Afventer godkendelse',
              },
              {
                id: SendoutStatus.Approved,
                label: 'Godkendt',
              },
              {
                id: SendoutStatus.Rejected,
                label: 'Afvist',
              },
              {
                id: SendoutStatus.Executing,
                label: 'Under udsendelse',
              },
              {
                id: SendoutStatus.Completed,
                label: 'Afsluttet',
              },
            ],
            (e) => e.status
          );

        this.sendoutsAccordion = { ...this.allSendoutsAccordion };


      },
      error: (error) => {
        console.error('Error fetching templates', error);
        this.snackbarService.show('Udsendelser kunne ikke hentes');
      },
      complete: () => {
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      },
    });
  };

  onCategoryFilterChange() {
    this.sendoutsAccordion = this.sharedFunctionalityService.onFilterChange(
      this.statusFilters,
      this.allSendoutsAccordion,
      'accordionHeading'
    );
  }
  handleLookbackChanged(lookbackPeriod: LookbackPeriod) {
    this.selectedLookback = lookbackPeriod;
    this.getAllSendouts();

  }
}
